import { useEffect, useRef, useState, type ReactElement } from 'react';
import type { CelulaLookup, ListaLotesOpAbastecimentoLookup, LoteOpAbastecimentoLookup } from '@pieltec/contracts';
import { apiFetch } from '../../lib/csrf-fetch';
import { abastecimentoApi } from '../../lib/estoque/abastecimento-api';
import { FormDialog, FormDialogBody, FormDialogContent, FormDialogDescription, FormDialogFooter, FormDialogHeader, FormDialogTitle } from '../../components/ui/form-dialog';
import { useToast } from '../../components/ui/toast';
import {
  SolicitarMaterialAcoes,
  SolicitarMaterialCorpo,
  descreverSolicitacaoCriada,
  enviarSolicitacao,
  primeiroCampoComErro,
  validarSolicitacaoMaterial,
  type CamposSolicitacaoMaterial,
  type ErrosSolicitacaoMaterial,
  type GuardaEnvioSolicitacao,
  type ModoSolicitacaoMaterial,
} from '../../components/coletor/solicitar-material-dialog';
import { CampoSelect } from '../../components/ui/campo-select';

const BOTAO = 'inline-flex min-h-11 items-center justify-center rounded-md border border-border px-4 font-semibold disabled:opacity-50';
const CAMPO = 'mt-1 min-h-11 w-full rounded-md border border-border bg-background p-2';
const PAGINA_INICIAL: ListaLotesOpAbastecimentoLookup = { itens: [], total: 0, pagina: 1, tamanhoPagina: 20 };

/** O par já escolhido contém ambos os UUIDs; o formulário nunca tenta inferir uma OP a partir de um lote. */
export function referenciaDoParLoteOp(par: LoteOpAbastecimentoLookup): string {
  return `Lote ${par.loteCodigo} · OP ${par.opNumero}`;
}

export function podeEnviarPedidoDesktop(modo: ModoSolicitacaoMaterial, loteOp: LoteOpAbastecimentoLookup | null, celulaCodigo: string): boolean {
  return modo === 'LOT_OP' ? loteOp !== null : celulaCodigo.trim() !== '';
}

export function PedirMaterialDesktopDialog({ aberto, onAbertoChange, onSolicitado }: {
  readonly aberto: boolean;
  readonly onAbertoChange: (aberto: boolean) => void;
  readonly onSolicitado: () => void;
}): ReactElement {
  const [modo, setModo] = useState<ModoSolicitacaoMaterial>('LOT_OP');
  const [busca, setBusca] = useState('');
  const [paginaLotes, setPaginaLotes] = useState(PAGINA_INICIAL);
  const [carregandoLotes, setCarregandoLotes] = useState(false);
  const [falhaLotes, setFalhaLotes] = useState<string | null>(null);
  const [loteOp, setLoteOp] = useState<LoteOpAbastecimentoLookup | null>(null);
  const [celulas, setCelulas] = useState<readonly CelulaLookup[]>([]);
  const [celulaCodigo, setCelulaCodigo] = useState('');
  const [campos, setCampos] = useState<CamposSolicitacaoMaterial>({ material: '', quantidade: '', unidade: '' });
  const [erros, setErros] = useState<ErrosSolicitacaoMaterial>({});
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const online = typeof navigator === 'undefined' || navigator.onLine;
  const toast = useToast();
  const chaveRef = useRef<{ readonly chave: string; readonly assinatura: string } | null>(null);
  const guardaRef = useRef<GuardaEnvioSolicitacao>({ emAndamento: false });

  const carregarLotes = async (pagina: number): Promise<void> => {
    setCarregandoLotes(true); setFalhaLotes(null);
    try {
      const params = new URLSearchParams({ pagina: String(pagina) });
      if (busca.trim()) params.set('busca', busca.trim());
      setPaginaLotes(await apiFetch<ListaLotesOpAbastecimentoLookup>(`/api/v1/estoque/lookups/lotes-op-abastecimento?${params.toString()}`));
    } catch {
      setFalhaLotes('Não foi possível consultar lotes e OPs liberados. Tente novamente.');
    } finally {
      setCarregandoLotes(false);
    }
  };

  useEffect(() => {
    if (!aberto) return;
    void carregarLotes(1);
    let vivo = true;
    void apiFetch<CelulaLookup[]>('/api/v1/estoque/lookups/celulas')
      .then((itens) => { if (vivo) setCelulas(itens); })
      .catch(() => { if (vivo) setErroGeral('Não foi possível consultar as células ativas. Tente novamente.'); });
    return () => { vivo = false; };
  // A abertura recarrega o estado atual da planta; a busca é feita pelo botão explícito abaixo.
  }, [aberto]);

  const alterar = (nome: keyof CamposSolicitacaoMaterial, valor: string): void => {
    setCampos((atual) => ({ ...atual, [nome]: valor }));
    setErros((atual) => (atual[nome] === undefined ? atual : { ...atual, [nome]: undefined }));
  };

  const enviar = async (): Promise<void> => {
    if (guardaRef.current.emAndamento) return;
    const encontrados = validarSolicitacaoMaterial(campos, modo);
    const primeiro = primeiroCampoComErro(encontrados);
    setErros(encontrados);
    if (primeiro) { setErroGeral(null); return; }
    if (!podeEnviarPedidoDesktop(modo, loteOp, celulaCodigo)) {
      setErroGeral(modo === 'LOT_OP' ? 'Selecione o par exato de lote e OP liberada.' : 'Selecione uma célula ativa.');
      return;
    }
    const assinatura = JSON.stringify([modo, loteOp?.loteId, loteOp?.opId, celulaCodigo, campos.material.trim(), campos.quantidade.trim().replace(',', '.'), campos.unidade.trim()]);
    const chaveAtual = chaveRef.current?.assinatura === assinatura ? chaveRef.current.chave : null;
    setEnviando(true); setErroGeral(null);
    try {
      const resultado = await enviarSolicitacao({
        api: abastecimentoApi,
        modo,
        entrada: modo === 'LOT_OP'
          ? { loteId: loteOp?.loteId, opId: loteOp?.opId, material: campos.material, quantidade: campos.quantidade, unidade: campos.unidade }
          : { celulaCodigo, material: campos.material, quantidade: campos.quantidade, unidade: campos.unidade },
        chaveAtual,
        guarda: guardaRef.current,
      });
      if (resultado.tipo === 'ignorada') return;
      chaveRef.current = resultado.chave === null ? null : { chave: resultado.chave, assinatura };
      if (resultado.tipo === 'criada') {
        const texto = descreverSolicitacaoCriada(resultado.solicitacao);
        toast.success({ title: texto.titulo, description: texto.descricao });
        onAbertoChange(false); onSolicitado();
      } else setErroGeral(resultado.mensagem);
    } finally {
      setEnviando(false);
    }
  };

  const referencia = modo === 'LOT_OP'
    ? (loteOp ? referenciaDoParLoteOp(loteOp) : 'Selecione um lote/OP liberada')
    : (celulas.find((celula) => celula.codigo === celulaCodigo) ? `Setor ${celulaCodigo}` : 'Selecione uma célula ativa');

  return (
    <FormDialog open={aberto} onOpenChange={onAbertoChange}>
      <FormDialogContent size="wide">
        <FormDialogHeader><FormDialogTitle>Pedir material</FormDialogTitle><FormDialogDescription>O pedido cria uma solicitação para o Almoxarifado; não movimenta o estoque.</FormDialogDescription></FormDialogHeader>
        <FormDialogBody className="space-y-4">
          <fieldset className="grid gap-3 rounded-md border border-border p-4 sm:grid-cols-2"><legend className="px-1 text-sm font-semibold">Destino do pedido</legend>
            <label className="flex items-center gap-2"><input type="radio" checked={modo === 'LOT_OP'} onChange={() => { setModo('LOT_OP'); setErroGeral(null); }} />Para um lote/OP</label>
            <label className="flex items-center gap-2"><input type="radio" checked={modo === 'SETOR'} onChange={() => { setModo('SETOR'); setErroGeral(null); }} />Para um setor</label>
          </fieldset>
          {modo === 'LOT_OP' ? <section aria-label="Buscar lote e OP" className="space-y-3 rounded-md border border-border p-4">
            <div className="flex flex-wrap gap-2"><label className="min-w-0 flex-1 text-sm font-semibold">Lote, OP ou material<input value={busca} onChange={(evento) => setBusca(evento.target.value)} className={CAMPO} maxLength={100} /></label><button type="button" className={BOTAO} onClick={() => void carregarLotes(1)} disabled={carregandoLotes}>Buscar</button></div>
            {falhaLotes && <p role="alert" className="text-sm text-danger">{falhaLotes}</p>}
            {carregandoLotes ? <p role="status" className="text-sm text-muted-foreground">Buscando pares liberados…</p> : <div className="space-y-2">{paginaLotes.itens.map((par) => <button type="button" key={`${par.loteId}:${par.opId}`} className={`block w-full rounded-md border p-3 text-left ${loteOp?.opId === par.opId ? 'border-primary' : 'border-border'}`} aria-pressed={loteOp?.opId === par.opId} onClick={() => { setLoteOp(par); setErroGeral(null); }}><span className="font-semibold">{referenciaDoParLoteOp(par)}</span><span className="ml-2 text-sm text-muted-foreground">{par.materialCodigo}</span></button>)}</div>}
            <div className="flex items-center justify-between gap-3"><button type="button" className={BOTAO} disabled={paginaLotes.pagina <= 1 || carregandoLotes} onClick={() => void carregarLotes(paginaLotes.pagina - 1)}>Anterior</button><span className="text-sm">Página {paginaLotes.pagina} · {paginaLotes.total} pares</span><button type="button" className={BOTAO} disabled={paginaLotes.pagina * paginaLotes.tamanhoPagina >= paginaLotes.total || carregandoLotes} onClick={() => void carregarLotes(paginaLotes.pagina + 1)}>Próxima</button></div>
          </section> : <label className="block text-sm font-semibold">Setor / célula<CampoSelect aria-label="Setor / célula" value={celulaCodigo} onValueChange={(valor) => { setCelulaCodigo(valor); setErroGeral(null); }} options={[{ value: '', label: 'Selecione uma célula ativa' }, ...celulas.map((celula) => ({ value: celula.codigo, label: `${celula.codigo} · ${celula.nome}` }))]} className="mt-1 min-h-11" /></label>}
          <SolicitarMaterialCorpo modo={modo} referencia={referencia} campos={campos} erros={erros} erroGeral={erroGeral} online={online} onAlterar={alterar} />
        </FormDialogBody>
        <FormDialogFooter><SolicitarMaterialAcoes enviando={enviando} online={online} onCancelar={() => onAbertoChange(false)} onEnviar={() => void enviar()} /></FormDialogFooter>
      </FormDialogContent>
    </FormDialog>
  );
}
